import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../firebase';
import { quickLoadCache, getCacheKey } from '../utils/localStorageCache';
import { repairParentLinkedStudentIds } from '../utils/repairParentLinkedStudentIds';
import { parentLinkingService } from '../services';

const useParentStore = create(
  persist(
    (set, get) => ({
      // Parent profile data
      profile: {
        userId: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        photoURL: '',
        relationship: '',
        occupation: '',
        languages: [],
        availability: {},
        interests: [],
        createdAt: null,
        updatedAt: null
      },

      // Linked children - initialized via setInitialData from SchoolContext cache
      linkedChildren: [],
      linkedChildrenLoading: false,
      hasLoadedChildren: false,

      // Parent permissions
      permissions: {
        canViewGrades: true,
        canViewAttendance: true,
        canViewBehavior: true,
        canContactTeacher: true,
        canUpdateProfile: true,
        canManageMessages: true
      },

      // Loading states
      isLoading: false,
      isUpdating: false,
      hasInitialized: false, // Tracks if setInitialData has been called
      error: null,

      // Pagination state
      childrenLimit: 10,              // Start with 10
      childrenHasMore: false,         // Track if more exist
      childrenLoadingMore: false,     // Loading state for UI
      totalChildrenCount: 0,          // Total available (for display)

      // Cache management
      lastChildrenUpdate: null,

      // Listeners
      unsubscribeProfile: null,
      unsubscribeChildren: null,

      // Actions
      initializeParent: async (userId) => {
        console.log('[parentStore] initializeParent called with userId:', userId);
        if (!userId) return;

        // Reset if different user (e.g., cached profile from previous session)
        const { hasInitialized, profile } = get();
        if (profile.userId && profile.userId !== userId) {
          console.log('[parentStore] User changed, resetting store:', profile.userId, '→', userId);
          get().reset();
        }

        // Prevent re-initialization if already initialized for THIS user
        if (hasInitialized && profile.userId === userId) {
          console.log('[parentStore] Already initialized for this user, skipping');
          return;
        }

        // Set hasInitialized IMMEDIATELY to prevent re-entry
        set({ isLoading: true, error: null, hasInitialized: true });

        try {
          // Load parent profile
          const profileDoc = await getDoc(doc(db, 'parents', userId));
          if (profileDoc.exists()) {
            set({
              profile: {
                userId,
                ...profileDoc.data()
              }
            });
          } else {
            // Profile doesn't exist yet (first time onboarding), but set userId
            set({
              profile: {
                userId,
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                photoURL: '',
                relationship: '',
                occupation: '',
                languages: [],
                availability: {},
                interests: [],
                createdAt: null,
                updatedAt: null
              }
            });
          }

          // Set up real-time listener for profile
          const unsubProfile = onSnapshot(
            doc(db, 'parents', userId),
            (doc) => {
              if (doc.exists()) {
                set({
                  profile: {
                    userId,
                    ...doc.data()
                  }
                });
              }
            }
          );

          // Load linked children
          const linksQuery = query(
            collection(db, 'parentChildLinks'),
            where('parentId', '==', userId),
            where('status', '==', 'active')
          );

          // Set up real-time listener for linked children
          const handleInitChildrenError = (error) => {
            console.error('[parentStore] initializeParent children listener error', {
              code: error.code,
              message: error.message
            });

            if (error.code === 'failed-precondition') {
              get().subscribeChildrenFallback(userId);
              return;
            }

            set({
              error: error.message,
              hasLoadedChildren: true,
              isLoading: false
            });
          };

          const unsubChildren = onSnapshot(linksQuery, async (snapshot) => {
            console.log(`[parentStore] initializeParent children listener fired: ${snapshot.size} links`);
            const children = [];

            for (const linkDoc of snapshot.docs) {
              const link = linkDoc.data();

              // Get student data from hierarchical collection (schools/{schoolId}/students/{studentId})
              // schoolId is stored in the link document
              if (!link.schoolId) {
                console.warn('[parentStore] Link missing schoolId:', linkDoc.id);
                continue;
              }

              const studentDoc = await getDoc(doc(db, 'schools', link.schoolId, 'students', link.studentId));
              if (studentDoc.exists()) {
                const student = studentDoc.data();
                children.push({
                  linkId: linkDoc.id,
                  ...link,
                  student: {
                    id: link.studentId,
                    firstName: student.firstName,
                    lastName: student.surname || student.lastName,
                    className: student.className,
                    studentNumber: student.studentNumber,
                    photoURL: student.photoURL || '',
                    ...student
                  }
                });
              } else {
                console.warn('[parentStore] Student not found:', {
                  schoolId: link.schoolId,
                  studentId: link.studentId
                });
              }
            }

            set({ linkedChildren: children, hasLoadedChildren: true, isLoading: false });
          }, handleInitChildrenError);

          set({
            unsubscribeProfile: unsubProfile,
            unsubscribeChildren: unsubChildren
            // isLoading: false moved into children listener to prevent flash of "no children"
          });

        } catch (error) {
          console.error('Error initializing parent:', error);
          set({
            error: error.message,
            isLoading: false
            // hasInitialized already set at the start to prevent re-entry even on error
          });
        }
      },

      // Set initial parent data from Cloud Function (no loading state)
      setInitialData: (parentData) => {
        if (!parentData) {
          console.warn('[parentStore] setInitialData called with no data');
          return;
        }

        console.log('[parentStore] Setting initial data from Cloud Function', {
          hasProfile: !!parentData.profile,
          linkedChildrenCount: parentData.linkedChildren?.length || 0
        });

        const userId = parentData.profile?.userId;

        // Reset if different user
        const { profile } = get();
        if (profile.userId && profile.userId !== userId) {
          console.log('[parentStore] User changed in setInitialData, resetting:', profile.userId, '→', userId);
          get().reset();
        }

        // Set profile and linked children immediately (no loading state)
        set({
          profile: {
            userId: userId || '',
            firstName: parentData.profile?.firstName || '',
            lastName: parentData.profile?.lastName || '',
            email: parentData.profile?.email || '',
            phone: parentData.profile?.phone || '',
            photoURL: parentData.profile?.photoURL || '',
            relationship: parentData.profile?.relationship || '',
            occupation: parentData.profile?.occupation || '',
            languages: parentData.profile?.languages || [],
            availability: parentData.profile?.availability || {},
            interests: parentData.profile?.interests || [],
            createdAt: parentData.profile?.createdAt || null,
            updatedAt: parentData.profile?.updatedAt || null
          },
          linkedChildren: parentData.linkedChildren || [],
          linkedChildrenLoading: false,
          hasLoadedChildren: true,
          isLoading: false,
          hasInitialized: true, // Mark as initialized
          error: null
        });

        // Repair linkedStudentIds in users/{uid} if absent or out of sync with linked children
        if (userId && (parentData.linkedChildren || []).length > 0) {
          repairParentLinkedStudentIds(userId, parentData.linkedChildren || [])
            .catch(e => console.warn('[parentStore] repair skipped:', e.message));
        }

        // Set up single consolidated real-time listener for future updates
        if (userId) {
          get().setupConsolidatedListener(userId);
        }
      },

      // Consolidated real-time listener (replaces duplicate listeners)
      setupConsolidatedListener: async (userId) => {
        // Clean up existing listeners first
        get().cleanup();

        console.log('[parentStore] Setting up consolidated real-time listener for:', userId);

        try {
          // Profile listener
          const unsubProfile = onSnapshot(
            doc(db, 'parents', userId),
            (doc) => {
              if (doc.exists()) {
                set({
                  profile: {
                    userId,
                    ...doc.data()
                  }
                });
              }
            }
          );

          // Children listener - optimized with batch fetching
          // Use dynamic limit from state for pagination
          const linksQuery = query(
            collection(db, 'parentChildLinks'),
            where('parentId', '==', userId),
            where('status', '==', 'active'),
            orderBy('linkedAt', 'desc'),  // Fixed: using 'linkedAt' not 'createdAt'
            limit(get().childrenLimit)
          );

          const handleChildrenListenerError = (error) => {
            console.error('[parentStore] children listener error', {
              code: error.code,
              message: error.message
            });

            if (error.code === 'failed-precondition') {
              get().subscribeChildrenFallback(userId);
              return;
            }

            set({
              error: error.message,
              linkedChildrenLoading: false,
              hasLoadedChildren: true,
              isLoading: false
            });
          };

          const unsubChildren = onSnapshot(linksQuery, async (snapshot) => {
            console.log(`[parentStore] Children listener fired: ${snapshot.size} links`);
            const currentLimit = get().childrenLimit;

            if (snapshot.empty) {
              set({
                linkedChildren: [],
                hasLoadedChildren: true  // Mark as loaded even when empty
              });
              quickLoadCache.save(getCacheKey('parent_children', userId), [], { lastUpdate: Date.now() });
              return;
            }

            // Batch fetch all student data at once (optimization)
            const studentIds = new Set();
            const linkDataMap = new Map();

            snapshot.docs.forEach(linkDoc => {
              const link = linkDoc.data();
              if (link.studentId && link.schoolId) {
                studentIds.add(link.studentId);
                linkDataMap.set(link.studentId, { linkId: linkDoc.id, ...link });
              }
            });

            const uniqueStudentIds = Array.from(studentIds);
            const studentPromises = uniqueStudentIds.map(studentId => {
              const linkInfo = linkDataMap.get(studentId);
              return getDoc(doc(db, 'schools', linkInfo.schoolId, 'students', studentId));
            });

            const studentDocs = await Promise.all(studentPromises);
            const studentDataMap = new Map();

            studentDocs.forEach((studentDoc, index) => {
              if (studentDoc.exists) {
                studentDataMap.set(uniqueStudentIds[index], studentDoc.data());
              }
            });

            const children = [];
            for (const [studentId, linkInfo] of linkDataMap.entries()) {
              const student = studentDataMap.get(studentId);
              if (student) {
                children.push({
                  linkId: linkInfo.linkId,
                  ...linkInfo,
                  student: {
                    id: studentId,
                    firstName: student.firstName,
                    lastName: student.surname || student.lastName,
                    className: student.className,
                    studentNumber: student.studentNumber,
                    photoURL: student.photoURL || '',
                    ...student
                  }
                });
              } else {
                console.warn('[parentStore] Student not found:', {
                  schoolId: linkInfo.schoolId,
                  studentId
                });
              }
            }

            // Check if more exist by trying to fetch limit + 1
            const checkMoreQuery = query(
              collection(db, 'parentChildLinks'),
              where('parentId', '==', userId),
              where('status', '==', 'active'),
              orderBy('linkedAt', 'desc'),
              limit(currentLimit + 1)
            );

            const checkSnapshot = await getDocs(checkMoreQuery);
            const hasMore = checkSnapshot.size > currentLimit;

            set({
              linkedChildren: children,
              childrenHasMore: hasMore,
              totalChildrenCount: checkSnapshot.size,  // For "Showing X of Y"
              hasLoadedChildren: true,
              lastChildrenUpdate: Date.now()
            });

            // Repair linkedStudentIds in users/{uid} if absent or out of sync
            repairParentLinkedStudentIds(userId, children)
              .catch(e => console.warn('[parentStore] repair skipped:', e.message));

            // Cache the results
            quickLoadCache.save(
              getCacheKey('parent_children', userId),
              children,
              { lastUpdate: Date.now() }
            );
          }, handleChildrenListenerError);

          // Store unsubscribe functions and mark as initialized
          set({
            unsubscribeProfile: unsubProfile,
            unsubscribeChildren: unsubChildren,
            hasInitialized: true,
            isLoading: false
          });

        } catch (error) {
          console.error('[parentStore] Error setting up consolidated listener:', error);
          set({ hasInitialized: true, isLoading: false }); // Still mark initialized to prevent loops
        }
      },

      // Update parent profile
      updateProfile: async (updates) => {
        const { profile } = get();
        if (!profile.userId) return;

        set({ isUpdating: true, error: null });

        try {
          const updatedProfile = {
            ...updates,
            updatedAt: serverTimestamp()
          };

          await setDoc(
            doc(db, 'parents', profile.userId),
            updatedProfile,
            { merge: true }
          );

          set({
            profile: { ...profile, ...updates },
            isUpdating: false
          });

          return true;
        } catch (error) {
          console.error('Error updating profile:', error);
          set({
            error: error.message,
            isUpdating: false
          });
          return false;
        }
      },

      // Link a new child
      linkChild: async (accessCode) => {
        const { profile } = get();
        console.log('[linkChild] Starting with:', { userId: profile.userId, accessCode });

        if (!profile.userId || !accessCode) {
          console.log('[linkChild] Missing userId or accessCode');
          return { success: false, alreadyLinked: false };
        }

        set({ isUpdating: true, error: null });

        try {
          const redemption = await parentLinkingService.redeemAccessCode(
            accessCode,
            profile.relationship || 'parent'
          );

          if (redemption.alreadyLinked) {
            const studentName = [
              redemption.studentData?.firstName,
              redemption.studentData?.lastName,
            ].filter(Boolean).join(' ');
            set({
              isUpdating: false,
              error: `Already linked to ${studentName || 'this child'}`
            });
            return {
              success: false,
              alreadyLinked: true,
              studentName
            };
          }

          set({ isUpdating: false });
          return { success: true, alreadyLinked: false };

        } catch (error) {
          console.error('Error linking child:', error);
          set({
            error: error.message,
            isUpdating: false
          });
          return { success: false, alreadyLinked: false };
        }
      },

      // Unlink a child
      unlinkChild: async (linkId) => {
        const { profile } = get();
        set({ isUpdating: true, error: null });

        try {
          // Get the link document to find the studentId
          const linkDoc = await getDoc(doc(db, 'parentChildLinks', linkId));
          if (!linkDoc.exists()) {
            throw new Error('Link not found');
          }

          const linkData = linkDoc.data();
          const studentId = linkData.studentId;

          // Update link status
          await setDoc(
            doc(db, 'parentChildLinks', linkId),
            { status: 'inactive', unlinkedAt: serverTimestamp() },
            { merge: true }
          );

          // Get current linkedStudentIds array from user document
          const userDoc = await getDoc(doc(db, 'users', profile.userId));
          const currentLinkedIds = userDoc.data()?.linkedStudentIds || [];

          // Remove the studentId from the array
          const updatedLinkedIds = currentLinkedIds.filter(id => id !== studentId);

          // Update user document to remove studentId from linkedStudentIds
          await setDoc(
            doc(db, 'users', profile.userId),
            {
              linkedStudentIds: updatedLinkedIds,
              updatedAt: serverTimestamp()
            },
            { merge: true }
          );
          console.log('[unlinkChild] Updated user document, removed studentId from linkedStudentIds');

          set({ isUpdating: false });
          return true;

        } catch (error) {
          console.error('Error unlinking child:', error);
          set({
            error: error.message,
            isUpdating: false
          });
          return false;
        }
      },

      // Get a specific linked child
      getLinkedChild: (studentId) => {
        const { linkedChildren } = get();
        return linkedChildren.find(link => link.studentId === studentId);
      },

      // Check if parent has any linked children
      hasLinkedChildren: () => {
        const { linkedChildren } = get();
        return linkedChildren.length > 0;
      },

      // Get statistics about linked children
      getChildrenStats: () => {
        const { linkedChildren } = get();
        return {
          total: linkedChildren.length,
          byClass: linkedChildren.reduce((acc, link) => {
            const className = link.student?.className || 'Unassigned';
            acc[className] = (acc[className] || 0) + 1;
            return acc;
          }, {})
        };
      },

      // Cleanup listeners
      cleanup: () => {
        const { unsubscribeProfile, unsubscribeChildren } = get();
        if (unsubscribeProfile) unsubscribeProfile();
        if (unsubscribeChildren) unsubscribeChildren();
        set({
          unsubscribeProfile: null,
          unsubscribeChildren: null
        });
      },

      // Load more children (pagination)
      loadMoreChildren: async () => {
        const { childrenLoadingMore, childrenHasMore, childrenLimit } = get();

        if (childrenLoadingMore || !childrenHasMore) return;

        set({ childrenLoadingMore: true });

        try {
          const newLimit = childrenLimit + 10;
          set({ childrenLimit: newLimit });

          // Listener will auto-refresh with new limit
          // Wait a tick for listener to fire
          await new Promise(resolve => setTimeout(resolve, 100));

        } finally {
          set({ childrenLoadingMore: false });
        }
      },

      // Fallback children listener — used when main listener fails with failed-precondition
      // (missing composite index). Subscribes without orderBy/limit and sorts client-side.
      subscribeChildrenFallback: (userId) => {
        console.log('[parentStore] subscribeChildrenFallback: subscribing without index constraints');

        const linksQuery = query(
          collection(db, 'parentChildLinks'),
          where('parentId', '==', userId),
          where('status', '==', 'active')
          // No orderBy, no limit — avoids composite index requirement
        );

        const unsubChildren = onSnapshot(linksQuery, async (snapshot) => {
          console.log(`[parentStore] Fallback children listener fired: ${snapshot.size} links`);
          const children = [];

          for (const linkDoc of snapshot.docs) {
            const link = linkDoc.data();
            if (!link.schoolId) {
              console.warn('[parentStore] Fallback: Link missing schoolId:', linkDoc.id);
              continue;
            }

            const studentDoc = await getDoc(doc(db, 'schools', link.schoolId, 'students', link.studentId));
            if (studentDoc.exists) {
              const student = studentDoc.data();
              children.push({
                linkId: linkDoc.id,
                ...link,
                student: {
                  id: link.studentId,
                  firstName: student.firstName,
                  lastName: student.surname || student.lastName,
                  className: student.className,
                  studentNumber: student.studentNumber,
                  photoURL: student.photoURL || '',
                  ...student
                }
              });
            } else {
              console.warn('[parentStore] Fallback: Student not found:', {
                schoolId: link.schoolId,
                studentId: link.studentId
              });
            }
          }

          // Sort by linkedAt descending (client-side, since no orderBy)
          children.sort((a, b) => {
            const aMs = a.linkedAt?.toMillis?.() ?? 0;
            const bMs = b.linkedAt?.toMillis?.() ?? 0;
            return bMs - aMs;
          });

          set({ linkedChildren: children, hasLoadedChildren: true });
        });

        set({ unsubscribeChildren: unsubChildren });
      },

      // Reset store
      reset: () => {
        get().cleanup();
        set({
          profile: {
            userId: '',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            photoURL: '',
            relationship: '',
            occupation: '',
            languages: [],
            availability: {},
            interests: [],
            createdAt: null,
            updatedAt: null
          },
          linkedChildren: [],
          permissions: {
            canViewGrades: true,
            canViewAttendance: true,
            canViewBehavior: true,
            canContactTeacher: true,
            canUpdateProfile: true,
            canManageMessages: true
          },
          isLoading: false,
          isUpdating: false,
          hasInitialized: false,
          error: null
        });
      }
    }),
    {
      name: 'parent-store',
      partialize: (state) => ({
        profile: state.profile,
        permissions: state.permissions
        // NOTE: linkedChildren and hasInitialized are NOT persisted
        // They must come fresh from Cloud Function / real-time listener each session
        // Persisting them caused stale cross-user data (duplicate children)
      })
    }
  )
);

// Patch the external setState so that replace:true calls (used in tests) do not
// accidentally remove action methods. Internal set() calls inside store closures
// always use merge mode and are NOT affected by this patch.
const _initialActions = Object.fromEntries(
  Object.entries(useParentStore.getInitialState())
    .filter(([, v]) => typeof v === 'function')
);
const _origSetState = useParentStore.setState;
useParentStore.setState = (partial, replace) => {
  if (replace === true && partial !== null && typeof partial === 'object') {
    // Preserve action functions from initial state while resetting data fields
    _origSetState({ ..._initialActions, ...partial }, false);
  } else {
    _origSetState(partial, replace);
  }
};

export default useParentStore;
